import React, { useState, useEffect, useRef } from 'react';
import { 
    Gauge, Fingerprint, Network, Workflow, ShieldAlert, ShieldCheck, 
    Terminal, RefreshCw, Search, Clock, Database, Server, Cpu, HardDrive, 
    Lock, Radio, Sparkles, UserX, KeyRound, BarChart3, 
    CheckCircle2, AlertTriangle, Layers, Orbit, Copy, Zap,
    ArrowDown, ArrowUpDown, Activity, Download, Calendar, Filter, RotateCcw
} from 'lucide-react';
import { API_BASE_URL } from '../../config';
import MigrationDetailsModal from '../dms/MigrationDetailsModal';
import './AdminPortal.css';

const fmtBytes = (bytes) => {
    const b = parseInt(bytes, 10);
    if (isNaN(b) || b <= 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(b) / Math.log(k));
    return parseFloat((b / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const getAuthHeaders = () => {
    const token = localStorage.getItem('dms_auth_token') || sessionStorage.getItem('dms_auth_token');
    return {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
    };
};

const authFetch = (url, options = {}) => {
    const headers = getAuthHeaders();
    return fetch(url, {
        ...options,
        headers: {
            ...headers,
            ...(options.headers || {})
        }
    });
};

export default function AdminPortalView({ theme: propTheme }) {
    const [theme, setTheme] = useState(() => {
        return propTheme || (typeof document !== 'undefined' ? document.documentElement.getAttribute('data-theme') : null) || localStorage.getItem('theme') || 'dark';
    });

    useEffect(() => {
        if (propTheme) {
            setTheme(propTheme);
        } else {
            const updateFromDom = () => {
                const current = document.documentElement.getAttribute('data-theme') || 'dark';
                setTheme(current);
            };
            const observer = new MutationObserver(updateFromDom);
            observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
            return () => observer.disconnect();
        }
    }, [propTheme]);

    const [activeTab, setActiveTab] = useState('overview'); // overview | users | connections | migrations | audit | live_logs
    const [stats, setStats] = useState(null);
    const [users, setUsers] = useState([]);
    const [connections, setConnections] = useState([]);
    const [migrations, setMigrations] = useState([]);
    const [auditLogs, setAuditLogs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedJobLogs, setSelectedJobLogs] = useState(null);
    const [inspectingJob, setInspectingJob] = useState(null);
    const [selectedConnectionVault, setSelectedConnectionVault] = useState(null);
    const [testingHealthId, setTestingHealthId] = useState(null);
    const [selectedTelemetryJob, setSelectedTelemetryJob] = useState(null);
    const [auditSearch, setAuditSearch] = useState('');
    const [logCategoryFilter, setLogCategoryFilter] = useState('all'); // all | migration | health | ssh | errors
    const [logSearchQuery, setLogSearchQuery] = useState('');
    const [selectedUserFilter, setSelectedUserFilter] = useState('all');
    const [selectedJobFilter, setSelectedJobFilter] = useState('all');
    const [selectedTimeRange, setSelectedTimeRange] = useState('live'); // 'live' | '15m' | '1h' | '6h' | '24h' | '7d' | 'all'
    const [hoveredVelocityPoint, setHoveredVelocityPoint] = useState(null);
    const [hoveredLagPoint, setHoveredLagPoint] = useState(null);
    const [logLimit, setLogLimit] = useState(500);
    const [platformLiveLogs, setPlatformLiveLogs] = useState([]);
    const [telemetryMetrics, setTelemetryMetrics] = useState(null);
    const [autoScroll, setAutoScroll] = useState(true);
    const [modalAutoScroll, setModalAutoScroll] = useState(true);
    const [logSortOrder, setLogSortOrder] = useState('asc'); // 'asc' = streaming (latest at bottom) | 'desc' = newest on top
    const [showJumpToLatest, setShowJumpToLatest] = useState(false);
    const [showModalJumpToLatest, setShowModalJumpToLatest] = useState(false);

    const terminalEndRef = useRef(null);
    const terminalContainerRef = useRef(null);
    const modalTerminalEndRef = useRef(null);
    const modalTerminalContainerRef = useRef(null);

    const filteredMigrationOptions = migrations.filter(job => {
        if (selectedUserFilter === 'all') return true;
        if (selectedUserFilter === 'system') return !job.owner_email || job.owner_email === 'System';
        return (job.owner_email || '').toLowerCase() === selectedUserFilter.toLowerCase();
    });

    useEffect(() => {
        fetchAllData();
    }, []);

    useEffect(() => {
        let interval;
        const isHistorical = selectedTimeRange !== 'live' && selectedTimeRange !== '15m';

        if (inspectingJob || activeTab === 'live_logs' || activeTab === 'overview') {
            if (inspectingJob) fetchJobLogsSilent(inspectingJob.id);
            if (activeTab === 'live_logs') fetchLivePlatformLogs();
            fetchTelemetryMetrics();

            const pollInterval = isHistorical ? 10000 : 2500;
            interval = setInterval(() => {
                if (inspectingJob) fetchJobLogsSilent(inspectingJob.id);
                if (activeTab === 'live_logs') fetchLivePlatformLogs();
                fetchTelemetryMetrics();
            }, pollInterval);
        }
        return () => clearInterval(interval);
    }, [inspectingJob, activeTab, selectedUserFilter, selectedJobFilter, selectedTimeRange, logLimit]);

    // Direct container auto-scroll (without window jerking / rubberbanding)
    useEffect(() => {
        if (autoScroll && logSortOrder === 'asc' && terminalContainerRef.current) {
            terminalContainerRef.current.scrollTop = terminalContainerRef.current.scrollHeight;
        }
    }, [platformLiveLogs, autoScroll, logSortOrder]);

    useEffect(() => {
        if (modalAutoScroll && modalTerminalContainerRef.current) {
            modalTerminalContainerRef.current.scrollTop = modalTerminalContainerRef.current.scrollHeight;
        }
    }, [selectedJobLogs, modalAutoScroll]);

    // User manual scroll detection: scrolling up pauses auto-scroll so previous logs can be read
    const handleTerminalScroll = () => {
        if (!terminalContainerRef.current) return;
        const { scrollTop, scrollHeight, clientHeight } = terminalContainerRef.current;
        const isNearBottom = scrollHeight - scrollTop - clientHeight < 60;
        if (!isNearBottom && logSortOrder === 'asc') {
            setAutoScroll(false);
            setShowJumpToLatest(true);
        } else if (isNearBottom && logSortOrder === 'asc') {
            setAutoScroll(true);
            setShowJumpToLatest(false);
        }
    };

    const handleModalTerminalScroll = () => {
        if (!modalTerminalContainerRef.current) return;
        const { scrollTop, scrollHeight, clientHeight } = modalTerminalContainerRef.current;
        const isNearBottom = scrollHeight - scrollTop - clientHeight < 60;
        if (!isNearBottom) {
            setModalAutoScroll(false);
            setShowModalJumpToLatest(true);
        } else {
            setModalAutoScroll(true);
            setShowModalJumpToLatest(false);
        }
    };

    const handleJumpToLatest = () => {
        setAutoScroll(true);
        setShowJumpToLatest(false);
        if (terminalContainerRef.current) {
            terminalContainerRef.current.scrollTop = terminalContainerRef.current.scrollHeight;
        }
    };

    const handleModalJumpToLatest = () => {
        setModalAutoScroll(true);
        setShowModalJumpToLatest(false);
        if (modalTerminalContainerRef.current) {
            modalTerminalContainerRef.current.scrollTop = modalTerminalContainerRef.current.scrollHeight;
        }
    };

    const fetchLivePlatformLogs = async () => {
        try {
            let url = `${API_BASE_URL}/api/admin/live-logs?userEmail=${encodeURIComponent(selectedUserFilter)}&jobId=${encodeURIComponent(selectedJobFilter)}&timeRange=${selectedTimeRange}&limit=${logLimit}`;
            if (logSearchQuery.trim()) {
                url += `&search=${encodeURIComponent(logSearchQuery.trim())}`;
            }
            const res = await authFetch(url);
            const data = await res.json();
            if (data.success) {
                setPlatformLiveLogs(data.logs);
            }
        } catch (err) { }
    };

    const fetchTelemetryMetrics = async () => {
        try {
            const url = `${API_BASE_URL}/api/admin/telemetry-metrics?userEmail=${encodeURIComponent(selectedUserFilter)}&jobId=${encodeURIComponent(selectedJobFilter)}&timeRange=${selectedTimeRange}`;
            const res = await authFetch(url);
            const data = await res.json();
            if (data.success) {
                setTelemetryMetrics(data);
            }
        } catch (err) { }
    };

    const handleUserFilterChange = (newUser) => {
        setSelectedUserFilter(newUser);
        if (newUser !== 'all' && selectedJobFilter !== 'all') {
            const currentJob = migrations.find(j => String(j.id) === String(selectedJobFilter));
            if (currentJob && (currentJob.owner_email || '').toLowerCase() !== newUser.toLowerCase()) {
                setSelectedJobFilter('all');
            }
        }
    };

    const handleTimeRangeChange = (newRange) => {
        setSelectedTimeRange(newRange);
        if (newRange !== 'live') {
            setAutoScroll(false);
        } else {
            setAutoScroll(true);
        }
    };

    const handleExportLogs = () => {
        if (!platformLiveLogs || platformLiveLogs.length === 0) return;
        const textData = platformLiveLogs.map(l => {
            const time = l.created_at ? new Date(l.created_at).toISOString() : 'N/A';
            const user = l.user_email || 'System';
            const job = l.job_name ? `[Job #${l.job_id || ''} ${l.job_name}]` : '';
            const lvl = (l.level || 'INFO').toUpperCase();
            return `[${time}] [${user}] ${job} [${lvl}] ${l.message}`;
        }).join('\n');

        const blob = new Blob([textData], { type: 'text/plain;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `datalift_logs_${selectedUserFilter}_${selectedTimeRange}_${new Date().toISOString().slice(0, 10)}.txt`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const handleResetFilters = () => {
        setSelectedUserFilter('all');
        setSelectedJobFilter('all');
        setSelectedTimeRange('live');
        setLogCategoryFilter('all');
        setLogSearchQuery('');
        setLogLimit(500);
        setAutoScroll(true);
    };

    const renderInteractiveTelemetryChart = ({
        dataSeries = [],
        dataKey = 'speedMb',
        unit = 'MB/s',
        color = '#10b981',
        fillId = 'grad-speed',
        activePoint,
        setActivePoint
    }) => {
        const width = 280;
        const height = 55;
        const padding = 5;
        const effectiveHeight = height - padding * 2;
        const effectiveWidth = width - padding * 2;

        const hasRealPoints = dataSeries && dataSeries.length > 0;
        const values = hasRealPoints ? dataSeries.map(d => (typeof d[dataKey] === 'number' ? d[dataKey] : 0)) : [0, 0, 0, 0, 0];
        const max = Math.max(...values, dataKey === 'speedMb' ? 1.0 : 10);
        const min = Math.min(0, ...values);
        const range = (max - min) || 1;

        const coords = values.map((val, idx) => {
            const x = padding + (idx / Math.max(1, values.length - 1)) * effectiveWidth;
            const y = padding + effectiveHeight - ((val - min) / range) * effectiveHeight;
            return { x, y, raw: dataSeries[idx] || {} };
        });

        const pathD = coords.reduce((acc, pt, idx) => {
            return idx === 0 ? `M ${pt.x},${pt.y}` : `${acc} L ${pt.x},${pt.y}`;
        }, '');

        const areaD = `${pathD} L ${coords[coords.length - 1]?.x || width},${height} L ${coords[0]?.x || 0},${height} Z`;
        const lastPt = coords[coords.length - 1] || { x: width - padding, y: height / 2 };

        const handleMouseMove = (e) => {
            if (!hasRealPoints || coords.length === 0) return;
            const rect = e.currentTarget.getBoundingClientRect();
            const mouseX = e.clientX - rect.left;
            const ratio = Math.max(0, Math.min(1, (mouseX - padding) / effectiveWidth));
            const nearestIdx = Math.min(Math.max(0, Math.round(ratio * (coords.length - 1))), coords.length - 1);
            const pt = coords[nearestIdx];
            setActivePoint({
                ...pt.raw,
                x: pt.x,
                y: pt.y,
                value: values[nearestIdx],
                unit,
                color
            });
        };

        const handleMouseLeave = () => {
            setActivePoint(null);
        };

        return (
            <div 
                className="interactive-chart-wrapper"
                onMouseMove={handleMouseMove}
                onMouseLeave={handleMouseLeave}
            >
                <svg viewBox={`0 0 ${width} ${height}`} className="telemetry-svg" preserveAspectRatio="none" style={{ height: `${height}px` }}>
                    <defs>
                        <linearGradient id={fillId} x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor={color} stopOpacity="0.38" />
                            <stop offset="100%" stopColor={color} stopOpacity="0.0" />
                        </linearGradient>
                    </defs>
                    <path d={areaD} fill={`url(#${fillId})`} />
                    <path d={pathD} fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                    
                    {activePoint ? (
                        <>
                            <line 
                                x1={activePoint.x} 
                                y1="0" 
                                x2={activePoint.x} 
                                y2={height} 
                                stroke={color} 
                                strokeWidth="1.5" 
                                strokeDasharray="3,3" 
                                opacity="0.85" 
                            />
                            <circle cx={activePoint.x} cy={activePoint.y} r="5" fill="#ffffff" stroke={color} strokeWidth="2.5" />
                            <circle cx={activePoint.x} cy={activePoint.y} r="8" fill="none" stroke={color} strokeWidth="1" opacity="0.4" />
                        </>
                    ) : (
                        <circle cx={lastPt.x} cy={lastPt.y} r="3.5" fill="#ffffff" stroke={color} strokeWidth="2" />
                    )}
                </svg>

                {/* X-Axis Time / Scope Guide */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.64rem', color: 'var(--admin-text-muted)', marginTop: '2px', padding: '0 2px' }}>
                    <span>{hasRealPoints ? dataSeries[0]?.time || 'T-Start' : 'No Pulses'}</span>
                    <span style={{ fontSize: '0.62rem', opacity: 0.85, fontWeight: 600 }}>
                        {activePoint ? `Hover: ${activePoint.time}` : `${dataSeries.length} pulse records`}
                    </span>
                    <span>{hasRealPoints ? dataSeries[dataSeries.length - 1]?.time || 'Latest' : 'Idle'}</span>
                </div>

                {/* Floating Interactive Hover Tooltip */}
                {activePoint && (
                    <div 
                        className="telemetry-hover-tooltip"
                        style={{
                            left: `${Math.min(Math.max(activePoint.x - 40, 10), width - 130)}px`,
                            bottom: `${height + 2}px`,
                            border: `1px solid ${color}`
                        }}
                    >
                        <div style={{ color: color, fontWeight: 700, fontSize: '0.78rem' }}>
                            {activePoint.value !== undefined ? (typeof activePoint.value === 'number' ? activePoint.value.toFixed(2) : activePoint.value) : 0} {unit}
                        </div>
                        <div style={{ fontSize: '0.66rem', opacity: 0.9, marginTop: '2px' }}>
                            {activePoint.time} • {activePoint.jobName || 'System'}
                        </div>
                        {activePoint.volumeMb !== undefined && (
                            <div style={{ fontSize: '0.63rem', color: 'var(--admin-text-muted)' }}>
                                Vol: {activePoint.volumeMb >= 1024 ? (activePoint.volumeMb / 1024).toFixed(2) + ' GB' : activePoint.volumeMb.toFixed(1) + ' MB'} | {activePoint.owner || 'System'}
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    };

    const renderSvgSparkline = (points, color = '#8b5cf6', fillId = 'grad-cpu') => {
        const defaultPts = [12, 14, 18, 16, 20, 19, 24, 22, 28, 26, 25, 29];
        const dataPts = (points && points.length > 0) ? points : defaultPts;
        const width = 240;
        const height = 55;
        const padding = 5;
        const effectiveHeight = height - padding * 2;
        const effectiveWidth = width - padding * 2;

        const max = Math.max(...dataPts, 1);
        const min = Math.min(0, Math.min(...dataPts));
        const range = (max - min) || 1;

        const coords = dataPts.map((val, idx) => {
            const x = padding + (idx / Math.max(1, dataPts.length - 1)) * effectiveWidth;
            const y = padding + effectiveHeight - ((val - min) / range) * effectiveHeight;
            return { x, y };
        });

        const pathD = coords.reduce((acc, pt, idx) => {
            return idx === 0 ? `M ${pt.x},${pt.y}` : `${acc} L ${pt.x},${pt.y}`;
        }, '');

        const areaD = `${pathD} L ${coords[coords.length - 1].x},${height} L ${coords[0].x},${height} Z`;
        const lastPt = coords[coords.length - 1];

        return (
            <svg viewBox={`0 0 ${width} ${height}`} className="telemetry-svg" preserveAspectRatio="none">
                <defs>
                    <linearGradient id={fillId} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={color} stopOpacity="0.35" />
                        <stop offset="100%" stopColor={color} stopOpacity="0.0" />
                    </linearGradient>
                </defs>
                <path d={areaD} fill={`url(#${fillId})`} />
                <path d={pathD} fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                <circle cx={lastPt.x} cy={lastPt.y} r="3.5" fill="#ffffff" stroke={color} strokeWidth="2" />
            </svg>
        );
    };

    const fetchJobLogsSilent = async (jobId) => {
        if (!jobId) return;
        try {
            const res = await authFetch(`${API_BASE_URL}/api/admin/jobs/${jobId}/logs`);
            const data = await res.json();
            if (data.success) {
                setSelectedJobLogs(data.logs);
            }
        } catch (err) { }
    };

    const fetchAllData = () => {
        fetchStats();
        fetchUsers();
        fetchGlobalConnections();
        fetchGlobalMigrations();
        fetchAuditLogs();
    };

    const fetchStats = async () => {
        try {
            const res = await authFetch(`${API_BASE_URL}/api/admin/stats`);
            const data = await res.json();
            if (data.success) setStats(data.stats);
        } catch (err) {
            console.error('Error fetching admin stats:', err);
        }
    };

    const fetchUsers = async () => {
        try {
            setLoading(true);
            const res = await authFetch(`${API_BASE_URL}/api/admin/users`);
            const data = await res.json();
            if (data.success) setUsers(data.users);
        } catch (err) {
            console.error('Error fetching users:', err);
        } finally {
            setLoading(false);
        }
    };

    const fetchGlobalConnections = async () => {
        try {
            const res = await authFetch(`${API_BASE_URL}/api/admin/global-connections`);
            const data = await res.json();
            if (data.success) setConnections(data.connections);
        } catch (err) {
            console.error('Error fetching global connections:', err);
        }
    };

    const fetchGlobalMigrations = async () => {
        try {
            const res = await authFetch(`${API_BASE_URL}/api/admin/global-migrations`);
            const data = await res.json();
            if (data.success) setMigrations(data.jobs);
        } catch (err) {
            console.error('Error fetching global migrations:', err);
        }
    };

    const fetchAuditLogs = async () => {
        try {
            const res = await authFetch(`${API_BASE_URL}/api/admin/audit-logs`);
            const data = await res.json();
            if (data.success) setAuditLogs(data.logs);
        } catch (err) {
            console.error('Error fetching audit logs:', err);
        }
    };

    const handleTestHealth = async (connId) => {
        try {
            setTestingHealthId(connId);
            const res = await authFetch(`${API_BASE_URL}/api/admin/connections/${connId}/test-health`, {
                method: 'POST'
            });
            const data = await res.json();
            if (data.success && data.health) {
                setConnections(prev => prev.map(c => c.id === connId ? {
                    ...c,
                    health_status: data.health.status,
                    health_error: data.health.error,
                    health_checked_at: data.health.checked_at
                } : c));
            }
        } catch (e) {
            console.error('Error testing connection health:', e);
        } finally {
            setTestingHealthId(null);
        }
    };

    const handleDeleteUser = async (userId, email) => {
        if (!window.confirm(`Are you sure you want to permanently delete user "${email}"? This action cannot be undone.`)) {
            return;
        }

        try {
            const res = await authFetch(`${API_BASE_URL}/api/admin/users/${userId}`, {
                method: 'DELETE'
            });
            const data = await res.json();
            if (data.success) {
                alert(`User ${email} deleted successfully.`);
                fetchAllData();
            } else {
                alert(`Failed to delete user: ${data.error}`);
            }
        } catch (err) {
            alert(`Error deleting user: ${err.message}`);
        }
    };

    const handleRoleChange = async (userId, newRole) => {
        try {
            const res = await authFetch(`${API_BASE_URL}/api/admin/users/${userId}/role`, {
                method: 'PUT',
                body: JSON.stringify({ role: newRole })
            });
            const data = await res.json();
            if (data.success) {
                fetchUsers();
            } else {
                alert(`Failed to update role: ${data.error}`);
            }
        } catch (err) {
            alert(`Error updating role: ${err.message}`);
        }
    };

    const inspectJobLogs = async (job) => {
        try {
            setInspectingJob(job);
            const res = await authFetch(`${API_BASE_URL}/api/admin/jobs/${job.id}/logs`);
            const data = await res.json();
            if (data.success) {
                setSelectedJobLogs(data.logs);
            } else {
                setSelectedJobLogs([{ message: `Failed to load logs: ${data.error}` }]);
            }
        } catch (err) {
            setSelectedJobLogs([{ message: `Error: ${err.message}` }]);
        }
    };

    const formatUsageTime = (mins) => {
        const num = parseInt(mins, 10);
        if (isNaN(num) || num <= 0) return '0 mins';
        if (num < 60) return `${num} mins`;
        const hrs = Math.floor(num / 60);
        const remMins = num % 60;
        return remMins > 0 ? `${hrs}h ${remMins}m` : `${hrs} hrs`;
    };

    const isUserActive = (timestamp) => {
        if (!timestamp) return false;
        const diff = (new Date() - new Date(timestamp)) / 1000 / 60;
        return diff <= 15; // Active within last 15 minutes
    };

    const isLight = theme === 'light';

    return (
        <div className={`admin-workspace-view ${isLight ? 'admin-theme-light' : 'admin-theme-dark'}`}>
            
            {/* Futuristic Header Banner */}
            <div className="admin-workspace-header">
                <div className="admin-title-group">
                    <div className="admin-header-top-badge">
                        <Sparkles size={12} color="#8b5cf6" /> ENTERPRISE CLOUD GOVERNANCE
                    </div>
                    <h2>
                        <div className="admin-header-icon-orb">
                            <Orbit size={24} color="#8b5cf6" />
                        </div>
                        Admin Command & Global Telemetry Center
                    </h2>
                    <p>Live enterprise telemetry, cross-cloud CDC replication monitoring, database vault security, and platform audit stream.</p>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap' }}>
                    <button className="btn-action-inspect" onClick={fetchAllData}>
                        <RefreshCw size={14} className={loading ? 'spin' : ''} /> Refresh Telemetry
                    </button>
                    <div className="admin-live-pulse">
                        <span className="pulse-dot"></span>
                        <Radio size={13} style={{ marginRight: '2px' }} /> LIVE TELEMETRY (2s)
                    </div>
                </div>
            </div>

            {/* Futuristic Navigation Tabs */}
            <div className="admin-nav-bar">
                <button 
                    className={`admin-nav-item ${activeTab === 'overview' ? 'active' : ''}`}
                    onClick={() => setActiveTab('overview')}
                >
                    <Gauge size={16} /> Overview & Health
                </button>
                <button 
                    className={`admin-nav-item ${activeTab === 'users' ? 'active' : ''}`}
                    onClick={() => setActiveTab('users')}
                >
                    <Fingerprint size={16} /> User Directory ({users.length})
                </button>
                <button 
                    className={`admin-nav-item ${activeTab === 'connections' ? 'active' : ''}`}
                    onClick={() => setActiveTab('connections')}
                >
                    <Network size={16} /> Global Connections ({connections.length})
                </button>
                <button 
                    className={`admin-nav-item ${activeTab === 'migrations' ? 'active' : ''}`}
                    onClick={() => setActiveTab('migrations')}
                >
                    <Workflow size={16} /> Global Migrations ({migrations.length})
                </button>
                <button 
                    className={`admin-nav-item ${activeTab === 'audit' ? 'active' : ''}`}
                    onClick={() => setActiveTab('audit')}
                >
                    <ShieldAlert size={16} /> Audit Trail ({auditLogs.length})
                </button>
                <button 
                    className={`admin-nav-item ${activeTab === 'live_logs' ? 'active' : ''}`}
                    onClick={() => setActiveTab('live_logs')}
                >
                    <Terminal size={16} /> Live Platform Stream
                </button>
            </div>

            {/* Main View Content */}
            <div className="admin-view-content">

                {/* TAB 1: SYSTEM OVERVIEW & TELEMETRY */}
                {activeTab === 'overview' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                        
                        {/* Top Metric HUD Cards */}
                        <div className="admin-grid-stats">
                            <div className="admin-card-glass purple">
                                <div className="metric-header">
                                    <span>Registered Platform Users</span>
                                    <div className="metric-orb">
                                        <Fingerprint size={20} color="#8b5cf6" />
                                    </div>
                                </div>
                                <div className="metric-value">{stats ? stats.totalUsers : users.length}</div>
                                <div style={{ fontSize: '0.78rem', color: 'var(--admin-text-secondary)', marginTop: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <span style={{ color: '#10b981', fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                        <CheckCircle2 size={13} color="#10b981" /> {users.filter(u => isUserActive(u.last_seen || u.last_login)).length} Active
                                    </span>
                                    <span>| {users.length} Total Directory</span>
                                </div>
                            </div>

                            <div className="admin-card-glass success">
                                <div className="metric-header">
                                    <span>Secured Database Vaults</span>
                                    <div className="metric-orb" style={{ background: 'rgba(16, 185, 129, 0.15)', borderColor: 'rgba(16, 185, 129, 0.35)' }}>
                                        <Database size={20} color="#10b981" />
                                    </div>
                                </div>
                                <div className="metric-value">{stats ? stats.totalConnections : connections.length}</div>
                                <div style={{ fontSize: '0.78rem', color: 'var(--admin-text-secondary)', marginTop: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <span style={{ color: '#10b981', fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                        <Lock size={12} color="#10b981" /> SSL/TLS & AES-256
                                    </span>
                                    <span>Encrypted Bastions</span>
                                </div>
                            </div>

                            <div className="admin-card-glass cyan">
                                <div className="metric-header">
                                    <span>Active Replication Pipelines</span>
                                    <div className="metric-orb" style={{ background: 'rgba(6, 182, 212, 0.15)', borderColor: 'rgba(6, 182, 212, 0.35)' }}>
                                        <Workflow size={20} color="#06b6d4" />
                                    </div>
                                </div>
                                <div className="metric-value">{stats ? stats.totalJobs : migrations.length}</div>
                                <div style={{ fontSize: '0.78rem', color: 'var(--admin-text-secondary)', marginTop: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <span style={{ color: '#0284c7', fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                        <Radio size={12} color="#0284c7" /> {stats ? stats.runningJobs : '0'} Active Stream(s)
                                    </span>
                                    <span>| CDC Debezium</span>
                                </div>
                            </div>

                            <div className="admin-card-glass danger">
                                <div className="metric-header">
                                    <span>Pipeline Integrity Status</span>
                                    <div className="metric-orb" style={{ background: 'rgba(239, 68, 68, 0.15)', borderColor: 'rgba(239, 68, 68, 0.35)' }}>
                                        <ShieldAlert size={20} color={stats && stats.failedJobs > 0 ? '#ef4444' : '#10b981'} />
                                    </div>
                                </div>
                                <div className="metric-value">{stats ? (stats.failedJobs > 0 ? `${stats.failedJobs} Alerts` : '100%') : '100%'}</div>
                                <div style={{ fontSize: '0.78rem', color: stats && stats.failedJobs > 0 ? '#ef4444' : '#10b981', marginTop: '8px', fontWeight: 600 }}>
                                    {stats && stats.failedJobs > 0 ? '⚠️ Action Required on Failed Jobs' : '✓ Zero Critical Schema Anomaly Alerts'}
                                </div>
                            </div>
                        </div>

                        {/* Middle Section: Real-Time Replication Throughput Chart & Engine Distribution */}
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: '1.5rem' }}>
                            
                            {/* Visual SVG Throughput Graph */}
                            <div className="admin-card-glass" style={{ minHeight: '280px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        <div className="metric-orb" style={{ background: 'rgba(6, 182, 212, 0.12)', borderColor: 'rgba(6, 182, 212, 0.3)' }}>
                                            <BarChart3 size={18} color="#06b6d4" />
                                        </div>
                                        <div>
                                            <h3 style={{ margin: 0, fontSize: '1.05rem', color: 'var(--admin-text-main)', fontWeight: 700 }}>Replication Velocity & IO Throughput</h3>
                                            <div style={{ fontSize: '0.78rem', color: 'var(--admin-text-muted)' }}>Real-Time Transfer Speed (MB/s Peak Telemetry)</div>
                                        </div>
                                    </div>
                                    <span style={{ fontSize: '0.78rem', color: '#10b981', background: 'rgba(16, 185, 129, 0.12)', border: '1px solid rgba(16, 185, 129, 0.3)', padding: '4px 12px', borderRadius: '14px', fontWeight: 700 }}>
                                        ⚡ Peak ~95.4 MB/s
                                    </span>
                                </div>

                                {/* Futuristic Area SVG Curve */}
                                <div style={{ width: '100%', height: '160px', position: 'relative' }}>
                                    <svg viewBox="0 0 500 150" style={{ width: '100%', height: '100%', overflow: 'visible' }}>
                                        <defs>
                                            <linearGradient id="throughputGlow" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="0%" stopColor="#06b6d4" stopOpacity="0.4" />
                                                <stop offset="100%" stopColor="#8b5cf6" stopOpacity="0.0" />
                                            </linearGradient>
                                        </defs>

                                        {/* Grid background lines */}
                                        <line x1="0" y1="30" x2="500" y2="30" stroke="var(--admin-grid-line)" strokeDasharray="4 4" />
                                        <line x1="0" y1="75" x2="500" y2="75" stroke="var(--admin-grid-line)" strokeDasharray="4 4" />
                                        <line x1="0" y1="120" x2="500" y2="120" stroke="var(--admin-grid-line)" strokeDasharray="4 4" />

                                        {/* Curve Area Fill */}
                                        <path
                                            d="M 0,130 Q 80,110 140,50 T 260,80 T 380,20 T 500,60 L 500,150 L 0,150 Z"
                                            fill="url(#throughputGlow)"
                                        />

                                        {/* Glowing Stroke Line */}
                                        <path
                                            d="M 0,130 Q 80,110 140,50 T 260,80 T 380,20 T 500,60"
                                            fill="none"
                                            stroke="#06b6d4"
                                            strokeWidth="3"
                                            style={{ filter: 'drop-shadow(0 0 8px rgba(6, 182, 212, 0.6))' }}
                                        />

                                        {/* Pulsing Peak Node Points */}
                                        <circle cx="140" cy="50" r="5" fill="#06b6d4" style={{ filter: 'drop-shadow(0 0 8px #06b6d4)' }} />
                                        <circle cx="260" cy="80" r="5" fill="#8b5cf6" style={{ filter: 'drop-shadow(0 0 8px #8b5cf6)' }} />
                                        <circle cx="380" cy="20" r="6" fill="#10b981" style={{ filter: 'drop-shadow(0 0 10px #10b981)' }} />
                                        <circle cx="500" cy="60" r="5" fill="#06b6d4" style={{ filter: 'drop-shadow(0 0 8px #06b6d4)' }} />
                                    </svg>
                                </div>

                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.74rem', color: 'var(--admin-text-muted)', fontFamily: 'monospace' }}>
                                    <span>T-60s</span>
                                    <span>T-45s</span>
                                    <span>T-30s</span>
                                    <span>T-15s</span>
                                    <span style={{ color: '#10b981', fontWeight: 700 }}>LIVE NOW</span>
                                </div>
                            </div>

                            {/* Database Engine Distribution HUD */}
                            <div className="admin-card-glass" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                                <div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '1rem' }}>
                                        <div className="metric-orb" style={{ background: 'rgba(139, 92, 246, 0.12)', borderColor: 'rgba(139, 92, 246, 0.3)' }}>
                                            <Layers size={18} color="#8b5cf6" />
                                        </div>
                                        <div>
                                            <h3 style={{ margin: 0, fontSize: '1.05rem', color: 'var(--admin-text-main)', fontWeight: 700 }}>Database Engine Allocation</h3>
                                            <div style={{ fontSize: '0.78rem', color: 'var(--admin-text-muted)' }}>Global Connection Engine Distribution</div>
                                        </div>
                                    </div>

                                    {(() => {
                                        const total = Math.max(1, connections.length);
                                        const pgCount = connections.filter(c => c.type === 'postgresql').length;
                                        const mysqlCount = connections.filter(c => c.type === 'mysql').length;
                                        const mssqlCount = connections.filter(c => c.type === 'mssql' || c.type === 'sqlserver').length;
                                        
                                        const pgPct = Math.round((pgCount / total) * 100);
                                        const mysqlPct = Math.round((mysqlCount / total) * 100);
                                        const mssqlPct = Math.round((mssqlCount / total) * 100);

                                        return (
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', marginTop: '1rem' }}>
                                                {/* PostgreSQL Meter */}
                                                <div>
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.82rem', fontWeight: 700, marginBottom: '4px' }}>
                                                        <span style={{ color: '#0284c7', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                            <Server size={14} /> PostgreSQL Engine
                                                        </span>
                                                        <span style={{ color: 'var(--admin-text-main)', fontFamily: 'monospace' }}>{pgCount} ({pgPct}%)</span>
                                                    </div>
                                                    <div style={{ height: '8px', background: 'var(--admin-card-inner)', borderRadius: '4px', overflow: 'hidden' }}>
                                                        <div style={{ height: '100%', width: `${pgPct}%`, background: 'linear-gradient(90deg, #38bdf8, #6366f1)', borderRadius: '4px', boxShadow: '0 0 10px rgba(56, 189, 248, 0.5)' }}></div>
                                                    </div>
                                                </div>

                                                {/* MySQL Meter */}
                                                <div>
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.82rem', fontWeight: 700, marginBottom: '4px' }}>
                                                        <span style={{ color: '#d97706', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                            <Database size={14} /> MySQL InnoDB Engine
                                                        </span>
                                                        <span style={{ color: 'var(--admin-text-main)', fontFamily: 'monospace' }}>{mysqlCount} ({mysqlPct}%)</span>
                                                    </div>
                                                    <div style={{ height: '8px', background: 'var(--admin-card-inner)', borderRadius: '4px', overflow: 'hidden' }}>
                                                        <div style={{ height: '100%', width: `${mysqlPct}%`, background: 'linear-gradient(90deg, #f59e0b, #d97706)', borderRadius: '4px', boxShadow: '0 0 10px rgba(245, 158, 11, 0.5)' }}></div>
                                                    </div>
                                                </div>

                                                {/* SQL Server Meter */}
                                                <div>
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.82rem', fontWeight: 700, marginBottom: '4px' }}>
                                                        <span style={{ color: '#8b5cf6', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                            <Layers size={14} /> Microsoft SQL Server
                                                        </span>
                                                        <span style={{ color: 'var(--admin-text-main)', fontFamily: 'monospace' }}>{mssqlCount} ({mssqlPct}%)</span>
                                                    </div>
                                                    <div style={{ height: '8px', background: 'var(--admin-card-inner)', borderRadius: '4px', overflow: 'hidden' }}>
                                                        <div style={{ height: '100%', width: `${mssqlPct}%`, background: 'linear-gradient(90deg, #a855f7, #c084fc)', borderRadius: '4px', boxShadow: '0 0 10px rgba(168, 85, 247, 0.5)' }}></div>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })()}
                                </div>
                            </div>
                        </div>

                        {/* System Telemetry & Core Runtime Grid */}
                        <div className="admin-card-glass">
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '1.25rem' }}>
                                <div className="metric-orb">
                                    <Cpu size={20} color="#8b5cf6" />
                                </div>
                                <div>
                                    <h3 style={{ margin: 0, fontSize: '1.1rem', color: 'var(--admin-text-main)', fontWeight: 700 }}>System Telemetry & Hardware Runtime HUD</h3>
                                    <div style={{ fontSize: '0.8rem', color: 'var(--admin-text-muted)' }}>Live Node.js Cluster Memory, Process Uptime, and Kafka Event Bus Metrics</div>
                                </div>
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1.25rem' }}>
                                <div className="admin-telemetry-box">
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                                        <strong style={{ color: 'var(--admin-text-muted)', fontSize: '0.78rem', textTransform: 'uppercase' }}>Process Uptime</strong>
                                        <Clock size={16} color="#06b6d4" />
                                    </div>
                                    <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#0284c7', fontFamily: 'monospace' }}>
                                        {stats ? `${Math.floor(stats.uptimeSeconds / 60)} mins` : 'Active'}
                                    </div>
                                    <span style={{ fontSize: '0.74rem', color: '#10b981', marginTop: '4px', display: 'inline-block' }}>✓ Zero Process Restarts</span>
                                </div>

                                <div className="admin-telemetry-box">
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                                        <strong style={{ color: 'var(--admin-text-muted)', fontSize: '0.78rem', textTransform: 'uppercase' }}>Node.js Memory Heap</strong>
                                        <HardDrive size={16} color="#8b5cf6" />
                                    </div>
                                    <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#8b5cf6', fontFamily: 'monospace' }}>
                                        {stats ? `${stats.memoryUsageMB} MB` : 'Optimal'}
                                    </div>
                                    <span style={{ fontSize: '0.74rem', color: '#0284c7', marginTop: '4px', display: 'inline-block' }}>⚡ Heap Buffer Optimal</span>
                                </div>

                                <div className="admin-telemetry-box">
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                                        <strong style={{ color: 'var(--admin-text-muted)', fontSize: '0.78rem', textTransform: 'uppercase' }}>Replication Streams</strong>
                                        <Radio size={16} color="#10b981" />
                                    </div>
                                    <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#10b981', fontFamily: 'monospace' }}>
                                        {stats ? stats.runningJobs : '0'} Active
                                    </div>
                                    <span style={{ fontSize: '0.74rem', color: '#10b981', marginTop: '4px', display: 'inline-block' }}>● CDC Debezium Active</span>
                                </div>

                                <div className="admin-telemetry-box">
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                                        <strong style={{ color: 'var(--admin-text-muted)', fontSize: '0.78rem', textTransform: 'uppercase' }}>Database Pool</strong>
                                        <CheckCircle2 size={16} color="#10b981" />
                                    </div>
                                    <div style={{ fontSize: '1.2rem', fontWeight: 800, color: '#10b981', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        PostgreSQL Core
                                    </div>
                                    <span style={{ fontSize: '0.74rem', color: 'var(--admin-text-secondary)', marginTop: '4px', display: 'inline-block' }}>Connection Pool Active</span>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* TAB 2: DETAILED USER DIRECTORY */}
                {activeTab === 'users' && (
                    <div className="admin-table-wrapper">
                        <table className="admin-data-table">
                            <thead>
                                <tr>
                                    <th>User Profile & ID</th>
                                    <th>Role & Status</th>
                                    <th>Auth Providers</th>
                                    <th>Registered Date</th>
                                    <th>Last Login & Activity</th>
                                    <th>Usage Time</th>
                                    <th>Connections</th>
                                    <th>Jobs</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {users.length > 0 ? (
                                    users.map(u => (
                                        <tr key={u.user_id}>
                                            <td>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                    <div className="admin-user-avatar">
                                                        {(u.name || u.email || 'U').charAt(0).toUpperCase()}
                                                    </div>
                                                    <div>
                                                        <div style={{ fontWeight: 700, color: 'var(--admin-text-main)', fontSize: '0.92rem' }}>{u.name || 'User'}</div>
                                                        <div style={{ fontSize: '0.8rem', color: 'var(--admin-text-secondary)' }}>{u.email}</div>
                                                        <div style={{ fontSize: '0.72rem', color: 'var(--admin-text-muted)', marginTop: '2px' }}>User ID: #{u.user_id}</div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <select 
                                                    value={u.role || 'Platform User'}
                                                    onChange={(e) => handleRoleChange(u.user_id, e.target.value)}
                                                    style={{ 
                                                        background: 'var(--admin-input-bg)', 
                                                        color: 'var(--admin-text-main)', 
                                                        border: '1px solid var(--admin-input-border)', 
                                                        borderRadius: '8px', 
                                                        padding: '5px 10px', 
                                                        marginBottom: '6px',
                                                        fontSize: '0.84rem',
                                                        fontWeight: 600,
                                                        cursor: 'pointer'
                                                    }}
                                                >
                                                    <option value="Platform User">Platform User</option>
                                                    <option value="Admin">Admin</option>
                                                </select>
                                                <div>
                                                    {u.verified ? (
                                                        <span className="badge-verified"><CheckCircle2 size={12} /> Verified</span>
                                                    ) : (
                                                        <span className="badge-unverified"><Clock size={12} /> Pending</span>
                                                    )}
                                                </div>
                                            </td>
                                            <td>
                                                {u.providers && u.providers.length > 0 ? (
                                                    u.providers.map((p, idx) => (
                                                        <span key={idx} className="provider-badge">{p}</span>
                                                    ))
                                                ) : (
                                                    <span className="provider-badge">Password</span>
                                                )}
                                            </td>
                                            <td>{u.created_at ? new Date(u.created_at).toLocaleDateString() : 'N/A'}</td>
                                            <td>
                                                <div style={{ color: 'var(--admin-text-main)', fontSize: '0.86rem', fontWeight: 500 }}>
                                                    {u.last_login ? new Date(u.last_login).toLocaleString() : 'Never'}
                                                </div>
                                                <div style={{ marginTop: '4px' }}>
                                                    {isUserActive(u.last_seen || u.last_login) ? (
                                                        <span className="badge-verified"><Radio size={11} /> Online</span>
                                                    ) : (
                                                        <span className="badge-unverified"><Clock size={11} /> Offline</span>
                                                    )}
                                                </div>
                                            </td>
                                            <td>
                                                <span className="pill-status completed">{formatUsageTime(u.total_session_minutes)}</span>
                                            </td>
                                            <td><strong style={{ color: 'var(--admin-text-main)', fontSize: '1rem' }}>{parseInt(u.total_connections, 10) || 0}</strong></td>
                                            <td><strong style={{ color: 'var(--admin-text-main)', fontSize: '1rem' }}>{parseInt(u.total_jobs, 10) || 0}</strong></td>
                                            <td>
                                                <button 
                                                    className="btn-action-danger"
                                                    onClick={() => handleDeleteUser(u.user_id, u.email)}
                                                >
                                                    <UserX size={14} /> Remove User
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="9" style={{ textAlign: 'center', padding: '3rem', color: 'var(--admin-text-secondary)' }}>
                                            No platform users registered yet.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* TAB 3: GLOBAL DATABASE CONNECTIONS */}
                {activeTab === 'connections' && (
                    <div className="admin-table-wrapper">
                        <table className="admin-data-table">
                            <thead>
                                <tr>
                                    <th>Connection Name & ID</th>
                                    <th>Engine & Role</th>
                                    <th>Host / Port</th>
                                    <th>Database Name</th>
                                    <th>Owner (User Email)</th>
                                    <th>Health & Active Status</th>
                                    <th>Security & Vault Encryption</th>
                                    <th>Created At</th>
                                </tr>
                            </thead>
                            <tbody>
                                {connections.length > 0 ? (
                                    connections.map(c => (
                                        <tr key={c.id}>
                                            <td>
                                                <div style={{ fontWeight: 700, color: 'var(--admin-text-main)', fontSize: '0.92rem' }}>{c.name}</div>
                                                <div style={{ fontSize: '0.75rem', color: 'var(--admin-text-muted)' }}>ID: #{c.id}</div>
                                            </td>
                                            <td>
                                                <span className="pill-status running" style={{ fontSize: '0.72rem', padding: '3px 10px', marginBottom: '4px' }}>
                                                    {c.type || 'postgresql'}
                                                </span>
                                                <div style={{ fontSize: '0.75rem', color: 'var(--admin-text-secondary)', textTransform: 'capitalize' }}>
                                                    {c.role || 'source'}
                                                </div>
                                            </td>
                                            <td>
                                                <div style={{ fontFamily: 'monospace', fontWeight: 600, fontSize: '0.86rem', color: 'var(--admin-text-main)' }}>
                                                    {c.host || '127.0.0.1'}
                                                </div>
                                                <div style={{ fontSize: '0.75rem', color: 'var(--admin-text-muted)' }}>Port: {c.port || 5432}</div>
                                            </td>
                                            <td>
                                                <strong style={{ color: 'var(--admin-text-main)' }}>{c.database_name || c.database || 'N/A'}</strong>
                                            </td>
                                            <td>
                                                <div style={{ fontWeight: 600, color: 'var(--admin-text-main)' }}>{c.owner_email || 'System'}</div>
                                            </td>
                                            <td>
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                                                    {c.health_status === 'connected' ? (
                                                        <span className="badge-verified">
                                                            <CheckCircle2 size={13} color="#10b981" /> Active & Connected
                                                        </span>
                                                    ) : c.health_status === 'failed' ? (
                                                        <span className="badge-unverified" style={{ color: '#ef4444', borderColor: 'rgba(239, 68, 68, 0.4)' }} title={c.health_error || 'Connection test failed'}>
                                                            <AlertTriangle size={13} color="#ef4444" /> Connection Failed
                                                        </span>
                                                    ) : (
                                                        <span className="badge-unverified">
                                                            <Clock size={13} /> Unknown / Pending
                                                        </span>
                                                    )}
                                                    <button
                                                        className="btn-action-inspect"
                                                        onClick={() => handleTestHealth(c.id)}
                                                        disabled={testingHealthId === c.id}
                                                        style={{ fontSize: '0.72rem', padding: '4px 10px', borderRadius: '8px', width: 'fit-content' }}
                                                    >
                                                        <RefreshCw size={11} className={testingHealthId === c.id ? 'spin' : ''} /> Test Health
                                                    </button>
                                                    {c.health_checked_at && (
                                                        <div style={{ fontSize: '0.7rem', color: 'var(--admin-text-muted)' }}>
                                                            Checked {new Date(c.health_checked_at).toLocaleTimeString()}
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                            <td>
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                                    {c.has_password ? (
                                                        <span className="badge-sec" style={{ color: '#10b981' }}>
                                                            <Lock size={12} color="#10b981" /> AES-256 Vault Encrypted
                                                        </span>
                                                    ) : (
                                                        <span className="badge-sec" style={{ color: '#d97706' }}>
                                                            <AlertTriangle size={12} color="#d97706" /> Password Redacted
                                                        </span>
                                                    )}
                                                    {c.use_ssh_tunnel || c.has_ssh_key ? (
                                                        <span className="badge-sec" style={{ color: '#8b5cf6' }}>
                                                            <KeyRound size={12} color="#8b5cf6" /> SSH Bastion Tunnel
                                                        </span>
                                                    ) : (
                                                        <span className="badge-sec" style={{ color: '#6366f1' }}>
                                                            <Server size={12} color="#6366f1" /> Public IP Allowlisted
                                                        </span>
                                                    )}
                                                    {c.is_ssl ? (
                                                        <span className="badge-sec" style={{ color: '#0284c7' }}>
                                                            <ShieldCheck size={12} color="#0284c7" /> SSL/TLS Transport
                                                        </span>
                                                    ) : (
                                                        <span className="badge-sec" style={{ color: 'var(--admin-text-muted)' }}>
                                                            <Zap size={12} color="var(--admin-text-muted)" /> Standard TCP Socket
                                                        </span>
                                                    )}
                                                    
                                                    <button 
                                                        className="btn-action-inspect" 
                                                        onClick={() => setSelectedConnectionVault(c)} 
                                                        style={{ marginTop: '6px', fontSize: '0.74rem' }}
                                                    >
                                                        <KeyRound size={13} color="#8b5cf6" /> Broadview Raw DB Vault
                                                    </button>
                                                </div>
                                            </td>
                                            <td>{new Date(c.created_at).toLocaleString()}</td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="8" style={{ textAlign: 'center', padding: '3rem', color: 'var(--admin-text-secondary)' }}>
                                            No global database connections recorded.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* TAB 4: GLOBAL MIGRATIONS & TELEMETRY */}
                {activeTab === 'migrations' && (
                    <div className="admin-table-wrapper">
                        <table className="admin-data-table">
                            <thead>
                                <tr>
                                    <th>Job ID & Name</th>
                                    <th>Owner (User Email)</th>
                                    <th>Type</th>
                                    <th>Status</th>
                                    <th>Progress & Volume Moved</th>
                                    <th>Execution Duration</th>
                                    <th>Created At</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {migrations.length > 0 ? (
                                    migrations.map(m => (
                                        <tr key={m.id}>
                                            <td>
                                                <div style={{ fontWeight: 700, color: 'var(--admin-text-main)', fontSize: '0.92rem' }}>{m.name}</div>
                                                <div style={{ fontSize: '0.78rem', color: 'var(--admin-text-muted)' }}>ID: #{m.id}</div>
                                            </td>
                                            <td>
                                                <div style={{ fontWeight: 600, color: 'var(--admin-text-main)' }}>{m.owner_email || 'System'}</div>
                                            </td>
                                            <td>
                                                <span className="badge-sec">{m.job_type || 'full+cdc'}</span>
                                            </td>
                                            <td>
                                                <span className={`pill-status ${m.status}`}>{m.status}</span>
                                            </td>
                                            <td>
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', minWidth: '160px' }}>
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', fontWeight: 700 }}>
                                                        <span style={{ color: 'var(--admin-text-main)' }}>{m.progress_num || m.progress || 0}%</span>
                                                        <span style={{ color: '#0284c7', fontFamily: 'monospace' }}>{fmtBytes(m.total_bytes_num || m.total_bytes || 0)}</span>
                                                    </div>
                                                    <div style={{ height: '7px', background: 'var(--admin-card-inner)', borderRadius: '4px', overflow: 'hidden' }}>
                                                        <div style={{ height: '100%', width: `${m.progress_num || m.progress || 0}%`, background: m.status === 'failed' ? '#ef4444' : m.status === 'completed' ? '#10b981' : 'linear-gradient(90deg, #6366f1, #06b6d4)', transition: 'width 0.3s' }}></div>
                                                    </div>
                                                    {(m.speed_bps_num || m.speed_bps) > 0 && (
                                                        <div style={{ fontSize: '0.72rem', color: '#10b981', fontFamily: 'monospace', fontWeight: 700 }}>
                                                            ⚡ {fmtBytes(m.speed_bps_num || m.speed_bps)}/s
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                            <td>
                                                <div style={{ fontWeight: 700, color: '#0284c7', fontSize: '0.88rem' }}>
                                                    {m.duration_formatted || '0s'}
                                                </div>
                                                <div style={{ fontSize: '0.74rem', color: 'var(--admin-text-muted)' }}>
                                                    {(m.status === 'running' || m.status === 'syncing') ? 'Active Runtime' : 'Total Execution'}
                                                </div>
                                            </td>
                                            <td>{new Date(m.created_at).toLocaleString()}</td>
                                            <td>
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                                    <button 
                                                        className="btn-action-inspect"
                                                        onClick={() => setSelectedTelemetryJob(m)}
                                                        style={{ fontSize: '0.74rem', padding: '5px 10px' }}
                                                    >
                                                        <BarChart3 size={13} color="#8b5cf6" /> Telemetry & Metrics
                                                    </button>
                                                    <button 
                                                        className="btn-action-inspect"
                                                        onClick={() => inspectJobLogs(m)}
                                                        style={{ fontSize: '0.74rem', padding: '5px 10px' }}
                                                    >
                                                        <Search size={13} /> Inspect Backend Logs
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="8" style={{ textAlign: 'center', padding: '3rem', color: 'var(--admin-text-secondary)' }}>
                                            No global migration jobs recorded yet.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* TAB 5: USER ENTRY AUDIT TRAIL */}
                {activeTab === 'audit' && (
                    <div>
                        <div style={{ marginBottom: '1.2rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
                            <div style={{ position: 'relative', width: '340px' }}>
                                <Search size={15} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--admin-text-muted)' }} />
                                <input
                                    type="text"
                                    placeholder="Filter audit events, email, or action..."
                                    value={auditSearch}
                                    onChange={(e) => setAuditSearch(e.target.value)}
                                    style={{ 
                                        padding: '9px 14px 9px 38px', 
                                        fontSize: '0.88rem', 
                                        width: '100%',
                                        background: 'var(--admin-input-bg)',
                                        color: 'var(--admin-text-main)',
                                        border: '1px solid var(--admin-input-border)',
                                        borderRadius: '10px',
                                        boxSizing: 'border-box'
                                    }}
                                />
                            </div>
                            <span style={{ fontSize: '0.82rem', color: 'var(--admin-text-secondary)', fontWeight: 500 }}>
                                Showing {auditLogs.filter(l => !auditSearch || (l.email || '').toLowerCase().includes(auditSearch.toLowerCase()) || (l.action || '').toLowerCase().includes(auditSearch.toLowerCase())).length} audit log entries
                            </span>
                        </div>
                        <div className="admin-table-wrapper">
                            <table className="admin-data-table">
                                <thead>
                                    <tr>
                                        <th>Timestamp</th>
                                        <th>User Email & Role</th>
                                        <th>Action Event</th>
                                        <th>Details / Metadata</th>
                                        <th>IP Address</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(() => {
                                        const filteredLogs = auditLogs.filter(log => {
                                            if (!auditSearch) return true;
                                            const term = auditSearch.toLowerCase();
                                            const email = (log.email || '').toLowerCase();
                                            const action = (log.action || '').toLowerCase();
                                            const details = JSON.stringify(log.details || {}).toLowerCase();
                                            return email.includes(term) || action.includes(term) || details.includes(term);
                                        });

                                        if (filteredLogs.length === 0) {
                                            return (
                                                <tr>
                                                    <td colSpan="5" style={{ textAlign: 'center', padding: '3rem', color: 'var(--admin-text-secondary)' }}>
                                                        {auditSearch ? `No audit trail entries matching "${auditSearch}".` : 'No audit trail entries recorded yet.'}
                                                    </td>
                                                </tr>
                                            );
                                        }

                                        return filteredLogs.map(log => (
                                            <tr key={log.id}>
                                                <td>{new Date(log.created_at).toLocaleString()}</td>
                                                <td>
                                                    <div style={{ fontWeight: 700, color: 'var(--admin-text-main)' }}>{log.email || log.user_name || 'Anonymous'}</div>
                                                    <div style={{ fontSize: '0.74rem', color: 'var(--admin-text-muted)' }}>{log.user_role || 'User'}</div>
                                                </td>
                                                <td>
                                                    <span className="pill-status active" style={{ fontSize: '0.74rem', padding: '3px 10px', fontWeight: 800 }}>{log.action}</span>
                                                </td>
                                                <td>
                                                    {log.details ? (
                                                        <pre style={{ margin: 0, fontSize: '0.76rem', fontFamily: 'monospace', color: 'var(--admin-text-secondary)', background: 'var(--admin-card-inner)', padding: '6px 10px', borderRadius: '6px', maxWidth: '340px', overflowX: 'auto', whiteSpace: 'pre-wrap', border: '1px solid var(--admin-card-border)' }}>
                                                            {typeof log.details === 'object' ? JSON.stringify(log.details) : log.details}
                                                        </pre>
                                                    ) : (
                                                        <span style={{ color: 'var(--admin-text-muted)', fontSize: '0.78rem' }}>—</span>
                                                    )}
                                                </td>
                                                <td>
                                                    <span className="badge-sec">{log.ip_address || '127.0.0.1'}</span>
                                                </td>
                                            </tr>
                                        ));
                                    })()}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {/* TAB 6: LIVE PLATFORM SYSTEM LOGS WITH USER FILTERING & BROAD TELEMETRY HUD */}
                {activeTab === 'live_logs' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                        
                        {/* Broad Real-Time Telemetry & Resource Metrics HUD */}
                        <div className="telemetry-hud-grid">
                            {/* Card 1: System CPU Load */}
                            <div className="telemetry-graph-card">
                                <div className="telemetry-card-top">
                                    <div>
                                        <div className="telemetry-card-label">
                                            <Cpu size={14} color="#8b5cf6" /> System CPU Load
                                        </div>
                                        <div className="telemetry-card-val" style={{ color: (telemetryMetrics?.hardware?.cpuPercent ?? (stats?.cpuPercent || 15.4)) > 80 ? '#ef4444' : (telemetryMetrics?.hardware?.cpuPercent ?? (stats?.cpuPercent || 15.4)) > 60 ? '#f59e0b' : 'var(--admin-text-main)' }}>
                                            {telemetryMetrics?.hardware?.cpuPercent ?? (stats?.cpuPercent || 15.4)}%
                                        </div>
                                        <div className="telemetry-card-sub">
                                            {telemetryMetrics?.hardware?.cpuCores ?? 8} Cores | {telemetryMetrics?.hardware?.cpuModel ? telemetryMetrics.hardware.cpuModel.split('@')[0].trim() : 'Active Multi-Core Engine'}
                                        </div>
                                    </div>
                                    <div className="metric-orb purple" style={{ width: '34px', height: '34px' }}>
                                        <Cpu size={17} color="#8b5cf6" />
                                    </div>
                                </div>
                                <div className="telemetry-chart-container">
                                    {renderSvgSparkline(
                                        (telemetryMetrics?.hardware?.telemetryHistory || []).map(h => h.cpu),
                                        '#8b5cf6',
                                        'grad-cpu-telemetry'
                                    )}
                                </div>
                            </div>

                            {/* Card 2: Memory Allocation */}
                            <div className="telemetry-graph-card">
                                <div className="telemetry-card-top">
                                    <div>
                                        <div className="telemetry-card-label">
                                            <HardDrive size={14} color="#06b6d4" /> Memory Allocation
                                        </div>
                                        <div className="telemetry-card-val">
                                            {telemetryMetrics?.hardware?.usedRamGB ?? 12.8} <span style={{ fontSize: '0.95rem', fontWeight: 600, color: 'var(--admin-text-secondary)' }}>/ {telemetryMetrics?.hardware?.totalRamGB ?? 16.0} GB</span>
                                        </div>
                                        <div className="telemetry-card-sub">
                                            Node Heap: {telemetryMetrics?.hardware?.heapUsedMB ?? 48} MB | System RAM: {telemetryMetrics?.hardware?.usedRamPercent ?? 80}%
                                        </div>
                                    </div>
                                    <div className="metric-orb cyan" style={{ width: '34px', height: '34px' }}>
                                        <HardDrive size={17} color="#06b6d4" />
                                    </div>
                                </div>
                                <div className="telemetry-chart-container">
                                    {renderSvgSparkline(
                                        (telemetryMetrics?.hardware?.telemetryHistory || []).map(h => h.usedRamPercent),
                                        '#06b6d4',
                                        'grad-ram-telemetry'
                                    )}
                                </div>
                            </div>

                            {/* Card 3: Replication Velocity (From Real Logs) */}
                            <div className="telemetry-graph-card">
                                <div className="telemetry-card-top">
                                    <div style={{ width: '100%' }}>
                                        <div className="telemetry-card-label" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                                            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                <Activity size={14} color="#10b981" /> Replication Velocity
                                            </span>
                                            <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                                                {selectedUserFilter !== 'all' && (
                                                    <span className="telemetry-scope-pill user" title={`Filtered by User: ${selectedUserFilter}`}>
                                                        👤 {selectedUserFilter === 'system' ? 'System' : selectedUserFilter.split('@')[0]}
                                                    </span>
                                                )}
                                                {selectedJobFilter !== 'all' && (
                                                    <span className="telemetry-scope-pill job" title={`Filtered by Job #${selectedJobFilter}`}>
                                                        ⚡ Job #{selectedJobFilter}
                                                    </span>
                                                )}
                                                {selectedTimeRange !== 'live' && (
                                                    <span className="telemetry-scope-pill time" title={`Time Window: ${selectedTimeRange.toUpperCase()}`}>
                                                        📅 {selectedTimeRange.toUpperCase()}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        <div className="telemetry-card-val" style={{ color: '#10b981' }}>
                                            {hoveredVelocityPoint ? (
                                                <span>
                                                    {hoveredVelocityPoint.value !== undefined ? (typeof hoveredVelocityPoint.value === 'number' ? hoveredVelocityPoint.value.toFixed(2) : hoveredVelocityPoint.value) : '0.00'} MB/s
                                                    <span style={{ fontSize: '0.72rem', color: 'var(--admin-text-muted)', fontWeight: 500, marginLeft: '6px' }}>
                                                        ({hoveredVelocityPoint.time})
                                                    </span>
                                                </span>
                                            ) : (
                                                (() => {
                                                    const pts = telemetryMetrics?.logsTelemetry?.throughputTimeSeries || [];
                                                    const latest = pts.length > 0 ? pts[pts.length - 1].speedMb : 0;
                                                    const peak = telemetryMetrics?.scope?.peakSpeedMb ?? telemetryMetrics?.logsTelemetry?.peakSpeedMb ?? 0;
                                                    return latest > 0 ? `${latest.toFixed(2)} MB/s` : (peak > 0 ? `${peak.toFixed(2)} MB/s (Peak)` : `0.00 MB/s`);
                                                })()
                                            )}
                                        </div>
                                        <div className="telemetry-card-sub">
                                            Peak: {(telemetryMetrics?.scope?.peakSpeedMb ?? telemetryMetrics?.logsTelemetry?.peakSpeedMb ?? 0).toFixed(2)} MB/s | Moved: {(() => {
                                                const mb = telemetryMetrics?.scope?.totalMovedMB ?? telemetryMetrics?.logsTelemetry?.totalMovedMB ?? 0;
                                                return mb >= 1024 ? (mb / 1024).toFixed(2) + ' GB' : mb.toFixed(1) + ' MB';
                                            })()} {telemetryMetrics?.scope?.totalJobs ? `| ${telemetryMetrics.scope.totalJobs} Jobs (${telemetryMetrics.scope.activeJobs || 0} Active)` : ''}
                                        </div>
                                    </div>
                                    <div className="metric-orb green" style={{ width: '34px', height: '34px', background: 'rgba(16, 185, 129, 0.15)', borderColor: 'rgba(16, 185, 129, 0.35)', flexShrink: 0, marginLeft: '10px' }}>
                                        <Activity size={17} color="#10b981" />
                                    </div>
                                </div>
                                <div className="telemetry-chart-container">
                                    {renderInteractiveTelemetryChart({
                                        dataSeries: telemetryMetrics?.logsTelemetry?.throughputTimeSeries || [],
                                        dataKey: 'speedMb',
                                        unit: 'MB/s',
                                        color: '#10b981',
                                        fillId: 'grad-speed-telemetry',
                                        activePoint: hoveredVelocityPoint,
                                        setActivePoint: setHoveredVelocityPoint
                                    })}
                                </div>
                            </div>

                            {/* Card 4: Replication Sync Lag (From Real Logs) */}
                            <div className="telemetry-graph-card">
                                <div className="telemetry-card-top">
                                    <div style={{ width: '100%' }}>
                                        <div className="telemetry-card-label" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                                            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                <Gauge size={14} color="#f59e0b" /> Sync Latency & Lag
                                            </span>
                                            <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                                                {selectedUserFilter !== 'all' && (
                                                    <span className="telemetry-scope-pill user">
                                                        👤 {selectedUserFilter === 'system' ? 'System' : selectedUserFilter.split('@')[0]}
                                                    </span>
                                                )}
                                                {selectedJobFilter !== 'all' && (
                                                    <span className="telemetry-scope-pill job">
                                                        ⚡ Job #{selectedJobFilter}
                                                    </span>
                                                )}
                                                {selectedTimeRange !== 'live' && (
                                                    <span className="telemetry-scope-pill time">
                                                        📅 {selectedTimeRange.toUpperCase()}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        <div className="telemetry-card-val" style={{ color: (hoveredLagPoint?.value ?? telemetryMetrics?.logsTelemetry?.latestLagMs ?? 0) > 1000 ? '#ef4444' : '#f59e0b' }}>
                                            {hoveredLagPoint ? (
                                                <span>
                                                    {hoveredLagPoint.value} <span style={{ fontSize: '0.95rem', fontWeight: 600 }}>ms</span>
                                                    <span style={{ fontSize: '0.72rem', color: 'var(--admin-text-muted)', fontWeight: 500, marginLeft: '6px' }}>
                                                        ({hoveredLagPoint.time})
                                                    </span>
                                                </span>
                                            ) : (
                                                <span>
                                                    {telemetryMetrics?.logsTelemetry?.latestLagMs ?? 0} <span style={{ fontSize: '0.95rem', fontWeight: 600 }}>ms</span>
                                                </span>
                                            )}
                                        </div>
                                        <div className="telemetry-card-sub">
                                            {(telemetryMetrics?.logsTelemetry?.latestLagMs || 0) === 0 ? '● Real-Time Synchronous Parity' : `Sub-second CDC Stream (${telemetryMetrics?.logsTelemetry?.latestLagMs}ms delay)`}
                                            {telemetryMetrics?.logsTelemetry?.totalPulsePoints ? ` | ${telemetryMetrics.logsTelemetry.totalPulsePoints} Samples` : ''}
                                        </div>
                                    </div>
                                    <div className="metric-orb yellow" style={{ width: '34px', height: '34px', background: 'rgba(245, 158, 11, 0.15)', borderColor: 'rgba(245, 158, 11, 0.35)', flexShrink: 0, marginLeft: '10px' }}>
                                        <Gauge size={17} color="#f59e0b" />
                                    </div>
                                </div>
                                <div className="telemetry-chart-container">
                                    {renderInteractiveTelemetryChart({
                                        dataSeries: telemetryMetrics?.logsTelemetry?.throughputTimeSeries || [],
                                        dataKey: 'lagMs',
                                        unit: 'ms',
                                        color: '#f59e0b',
                                        fillId: 'grad-lag-telemetry',
                                        activePoint: hoveredLagPoint,
                                        setActivePoint: setHoveredLagPoint
                                    })}
                                </div>
                            </div>
                        </div>

                        {/* Control Bar: User Selector + Migration Job Filter + Time Range + Log Depth + Stream Filters */}
                        <div style={{ background: 'var(--admin-card-bg)', padding: '1.2rem', borderRadius: '16px', border: '1px solid var(--admin-card-border)', display: 'flex', flexDirection: 'column', gap: '1rem', boxShadow: 'var(--admin-card-shadow)' }}>
                            
                            {/* Row 1: Scoping Controls */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap' }}>
                                
                                {/* User Filter */}
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', minWidth: '220px', flex: '1 1 220px' }}>
                                    <label style={{ fontSize: '0.74rem', fontWeight: 700, color: 'var(--admin-text-muted)', textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: '5px' }}>
                                        <Fingerprint size={12} color="#8b5cf6" /> Scope by User / Account:
                                    </label>
                                    <select
                                        value={selectedUserFilter}
                                        onChange={(e) => handleUserFilterChange(e.target.value)}
                                        style={{
                                            padding: '8px 12px',
                                            fontSize: '0.82rem',
                                            fontWeight: 600,
                                            background: 'var(--admin-input-bg)',
                                            color: 'var(--admin-text-main)',
                                            border: '1px solid var(--admin-input-border)',
                                            borderRadius: '10px',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        <option value="all">🌐 All Platform Activity (Global)</option>
                                        <option value="system">⚙️ Core Platform & System Engine Only</option>
                                        {users.length > 0 && (
                                            <optgroup label="Filtered By Registered User">
                                                {users.map(u => (
                                                    <option key={u.user_id} value={u.email}>
                                                        👤 {u.name || u.email} ({u.email}) — [{u.total_jobs || 0} Jobs]
                                                    </option>
                                                ))}
                                            </optgroup>
                                        )}
                                    </select>
                                </div>

                                {/* Migration Job Selector */}
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', minWidth: '220px', flex: '1 1 220px' }}>
                                    <label style={{ fontSize: '0.74rem', fontWeight: 700, color: 'var(--admin-text-muted)', textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: '5px' }}>
                                        <Zap size={12} color="#0284c7" /> Filter by Migration Job:
                                    </label>
                                    <select
                                        value={selectedJobFilter}
                                        onChange={(e) => setSelectedJobFilter(e.target.value)}
                                        style={{
                                            padding: '8px 12px',
                                            fontSize: '0.82rem',
                                            fontWeight: 600,
                                            background: 'var(--admin-input-bg)',
                                            color: 'var(--admin-text-main)',
                                            border: '1px solid var(--admin-input-border)',
                                            borderRadius: '10px',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        <option value="all">⚡ All Migration Jobs ({filteredMigrationOptions.length})</option>
                                        {filteredMigrationOptions.map(j => (
                                            <option key={j.id} value={j.id}>
                                                #{j.id} - {j.name} [{j.status.toUpperCase()}]{selectedUserFilter === 'all' && j.owner_email ? ` • ${j.owner_email}` : ''}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {/* Time Window / Historical Period Selector */}
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', minWidth: '190px', flex: '1 1 190px' }}>
                                    <label style={{ fontSize: '0.74rem', fontWeight: 700, color: 'var(--admin-text-muted)', textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: '5px' }}>
                                        <Clock size={12} color="#f59e0b" /> Time Range / History:
                                    </label>
                                    <select
                                        value={selectedTimeRange}
                                        onChange={(e) => handleTimeRangeChange(e.target.value)}
                                        style={{
                                            padding: '8px 12px',
                                            fontSize: '0.82rem',
                                            fontWeight: 600,
                                            background: selectedTimeRange !== 'live' ? 'rgba(245, 158, 11, 0.12)' : 'var(--admin-input-bg)',
                                            color: selectedTimeRange !== 'live' ? '#d97706' : 'var(--admin-text-main)',
                                            border: selectedTimeRange !== 'live' ? '1px solid rgba(245, 158, 11, 0.4)' : '1px solid var(--admin-input-border)',
                                            borderRadius: '10px',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        <option value="live">🔴 Live Stream (Real-Time 2s)</option>
                                        <option value="15m">⏱️ Last 15 Minutes</option>
                                        <option value="1h">⏱️ Last 1 Hour</option>
                                        <option value="6h">🕒 Last 6 Hours</option>
                                        <option value="24h">📅 Last 24 Hours</option>
                                        <option value="7d">📆 Last 7 Days</option>
                                        <option value="all">📜 All Historical Records</option>
                                    </select>
                                </div>

                                {/* Log Depth Selector */}
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', minWidth: '130px' }}>
                                    <label style={{ fontSize: '0.74rem', fontWeight: 700, color: 'var(--admin-text-muted)', textTransform: 'uppercase' }}>Log Depth:</label>
                                    <select
                                        value={logLimit}
                                        onChange={(e) => setLogLimit(Number(e.target.value))}
                                        title="Configure how many previous logs to load from server"
                                        style={{
                                            padding: '8px 12px',
                                            fontSize: '0.82rem',
                                            fontWeight: 600,
                                            background: 'var(--admin-input-bg)',
                                            color: 'var(--admin-text-main)',
                                            border: '1px solid var(--admin-input-border)',
                                            borderRadius: '10px',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        <option value={200}>200 Logs</option>
                                        <option value={500}>500 Logs (Default)</option>
                                        <option value={1000}>1,000 Previous Logs</option>
                                        <option value={2000}>2,000 Previous Logs</option>
                                        <option value={2500}>2,500 Previous Logs (Deep)</option>
                                    </select>
                                </div>
                            </div>

                            {/* Row 2: Stream Category Filters + Search Bar + Actions */}
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px', paddingTop: '8px', borderTop: '1px solid var(--admin-card-border)' }}>
                                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
                                    <span style={{ fontSize: '0.74rem', fontWeight: 700, color: 'var(--admin-text-muted)', marginRight: '4px' }}>STREAM:</span>
                                    {[
                                        { id: 'all', label: 'All Streams' },
                                        { id: 'migration', label: 'Migration & CDC' },
                                        { id: 'health', label: 'Health Probes' },
                                        { id: 'ssh', label: 'SSH Tunnels' },
                                        { id: 'errors', label: 'Failures Only' }
                                    ].map(cat => (
                                        <button
                                            key={cat.id}
                                            onClick={() => setLogCategoryFilter(cat.id)}
                                            style={{
                                                padding: '6px 12px',
                                                borderRadius: '8px',
                                                fontSize: '0.78rem',
                                                fontWeight: logCategoryFilter === cat.id ? 700 : 500,
                                                background: logCategoryFilter === cat.id ? 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)' : 'var(--admin-card-inner)',
                                                color: logCategoryFilter === cat.id ? '#ffffff' : 'var(--admin-text-secondary)',
                                                border: logCategoryFilter === cat.id ? '1px solid #8b5cf6' : '1px solid var(--admin-card-border)',
                                                cursor: 'pointer',
                                                transition: 'all 0.2s ease'
                                            }}
                                        >
                                            {cat.label}
                                        </button>
                                    ))}

                                    {/* Reset Filters Pill */}
                                    {(selectedUserFilter !== 'all' || selectedJobFilter !== 'all' || selectedTimeRange !== 'live' || logSearchQuery || logCategoryFilter !== 'all') && (
                                        <button
                                            onClick={handleResetFilters}
                                            title="Reset all filters back to global real-time stream"
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '4px',
                                                padding: '6px 10px',
                                                borderRadius: '8px',
                                                fontSize: '0.75rem',
                                                fontWeight: 600,
                                                background: 'rgba(239, 68, 68, 0.1)',
                                                color: '#ef4444',
                                                border: '1px solid rgba(239, 68, 68, 0.3)',
                                                cursor: 'pointer',
                                                marginLeft: '4px'
                                            }}
                                        >
                                            <RotateCcw size={11} /> Reset Filters
                                        </button>
                                    )}
                                </div>

                                {/* Right Side: Search + Auto-scroll toggle + Sort Order + Export */}
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                    <div style={{ position: 'relative', width: '200px' }}>
                                        <Search size={13} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: 'var(--admin-text-muted)' }} />
                                        <input
                                            type="text"
                                            placeholder="Search logs / jobs..."
                                            value={logSearchQuery}
                                            onChange={(e) => setLogSearchQuery(e.target.value)}
                                            style={{
                                                padding: '6px 10px 6px 30px',
                                                fontSize: '0.8rem',
                                                background: 'var(--admin-input-bg)',
                                                color: 'var(--admin-text-main)',
                                                border: '1px solid var(--admin-input-border)',
                                                borderRadius: '8px',
                                                width: '100%',
                                                boxSizing: 'border-box'
                                            }}
                                        />
                                    </div>

                                    <button
                                        onClick={() => setAutoScroll(!autoScroll)}
                                        title="Toggle automatic scrolling to latest logs"
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '5px',
                                            padding: '6px 10px',
                                            borderRadius: '8px',
                                            fontSize: '0.75rem',
                                            fontWeight: 700,
                                            background: autoScroll ? 'rgba(16, 185, 129, 0.12)' : 'var(--admin-card-inner)',
                                            color: autoScroll ? '#10b981' : 'var(--admin-text-muted)',
                                            border: `1px solid ${autoScroll ? 'rgba(16, 185, 129, 0.4)' : 'var(--admin-card-border)'}`,
                                            cursor: 'pointer'
                                        }}
                                    >
                                        <ArrowDown size={12} /> Auto-Scroll: {autoScroll ? 'ON' : 'PAUSED'}
                                    </button>

                                    <button
                                        onClick={() => setLogSortOrder(logSortOrder === 'asc' ? 'desc' : 'asc')}
                                        title="Switch between live streaming view and newest-first order"
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '5px',
                                            padding: '6px 10px',
                                            borderRadius: '8px',
                                            fontSize: '0.75rem',
                                            fontWeight: 600,
                                            background: 'var(--admin-card-inner)',
                                            color: 'var(--admin-text-secondary)',
                                            border: '1px solid var(--admin-card-border)',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        <ArrowUpDown size={12} /> {logSortOrder === 'asc' ? 'Streaming' : 'Newest First'}
                                    </button>

                                    <button
                                        onClick={handleExportLogs}
                                        title="Download loaded log entries as text file"
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '5px',
                                            padding: '6px 10px',
                                            borderRadius: '8px',
                                            fontSize: '0.75rem',
                                            fontWeight: 600,
                                            background: 'var(--admin-card-inner)',
                                            color: '#8b5cf6',
                                            border: '1px solid var(--admin-card-border)',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        <Download size={12} /> Export
                                    </button>

                                    {selectedTimeRange === 'live' ? (
                                        <div className="admin-live-pulse" style={{ padding: '5px 12px', fontSize: '0.72rem' }}>
                                            <span className="pulse-dot"></span>
                                            <Radio size={12} style={{ marginRight: '2px' }} /> LIVE (2s)
                                        </div>
                                    ) : (
                                        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '5px 12px', borderRadius: '20px', background: 'rgba(245, 158, 11, 0.15)', color: '#d97706', border: '1px solid rgba(245, 158, 11, 0.35)', fontSize: '0.72rem', fontWeight: 700 }}>
                                            <Clock size={12} /> HISTORICAL
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Historical Inspection Notice Banner */}
                        {selectedTimeRange !== 'live' && (
                            <div className="telemetry-history-banner">
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <Clock size={16} />
                                    <span>
                                        <strong>Historical Inspection Mode:</strong> Showing telemetry and logs for <u>{selectedTimeRange.toUpperCase()}</u>. Auto-scroll is paused to prevent unexpected scroll jumping.
                                    </span>
                                </div>
                                <button
                                    onClick={() => handleTimeRangeChange('live')}
                                    style={{
                                        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                                        color: '#ffffff',
                                        border: 'none',
                                        borderRadius: '8px',
                                        padding: '5px 12px',
                                        fontSize: '0.76rem',
                                        fontWeight: 700,
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '5px',
                                        boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)'
                                    }}
                                >
                                    <Radio size={12} /> Resume Real-Time Live Stream
                                </button>
                            </div>
                        )}

                        {/* Live Terminal Window (Light & Dark theme aware, Auto-scrolling) */}
                        <div 
                            ref={terminalContainerRef}
                            onScroll={handleTerminalScroll}
                            className="terminal-window" 
                            style={{ height: '520px', position: 'relative' }}
                        >
                            {(() => {
                                const rawList = platformLiveLogs || [];
                                const ordered = logSortOrder === 'desc' ? [...rawList].reverse() : rawList;

                                const filtered = ordered.filter(l => {
                                    const msg = (l.message || '').toLowerCase();
                                    const lvl = (l.level || '').toLowerCase();
                                    const user = (l.user_email || '').toLowerCase();
                                    const userName = (l.user_name || '').toLowerCase();
                                    const search = logSearchQuery.toLowerCase();

                                    if (search && !msg.includes(search) && !lvl.includes(search) && !user.includes(search) && !userName.includes(search)) return false;

                                    if (logCategoryFilter === 'migration') {
                                        return msg.includes('migrated') || msg.includes('streamed') || msg.includes('cdc') || msg.includes('table') || msg.includes('bulk') || msg.includes('full load') || msg.includes('initializing') || msg.includes('cutover');
                                    }
                                    if (logCategoryFilter === 'health') {
                                        return msg.includes('health') || msg.includes('probe') || msg.includes('ping') || msg.includes('socket') || msg.includes('connect');
                                    }
                                    if (logCategoryFilter === 'ssh') {
                                        return msg.includes('ssh') || msg.includes('tunnel') || msg.includes('bastion') || msg.includes('key') || msg.includes('permission') || msg.includes('timed out');
                                    }
                                    if (logCategoryFilter === 'errors') {
                                        return lvl === 'error' || lvl === 'warning' || msg.includes('failed') || msg.includes('error') || msg.includes('overflow') || msg.includes('denied') || msg.includes('exception');
                                    }
                                    return true;
                                });

                                if (filtered.length === 0) {
                                    return (
                                        <div style={{ color: 'var(--admin-text-muted)', textAlign: 'center', paddingTop: '100px' }}>
                                            <div style={{ fontSize: '1rem', fontWeight: 600 }}>No live log events matching active filters.</div>
                                            <div style={{ fontSize: '0.82rem', marginTop: '8px', color: 'var(--admin-text-secondary)' }}>Logs stream live automatically as user replication jobs and health checks pulse.</div>
                                        </div>
                                    );
                                }

                                return (
                                    <>
                                        {filtered.map((l, i) => {
                                            const lvl = (l.level || 'info').toLowerCase();
                                            let badgeColor = isLight ? '#0284c7' : '#38bdf8';
                                            let bgHighlight = 'transparent';
                                            if (lvl === 'error' || (l.message || '').toLowerCase().includes('failed') || (l.message || '').toLowerCase().includes('error')) {
                                                badgeColor = '#ef4444';
                                                bgHighlight = isLight ? 'rgba(239, 68, 68, 0.08)' : 'rgba(239, 68, 68, 0.14)';
                                            } else if (lvl === 'warning' || (l.message || '').toLowerCase().includes('warning')) {
                                                badgeColor = '#f59e0b';
                                                bgHighlight = isLight ? 'rgba(245, 158, 11, 0.08)' : 'rgba(245, 158, 11, 0.1)';
                                            } else if (lvl === 'success' || (l.message || '').toLowerCase().includes('completed') || (l.message || '').toLowerCase().includes('success')) {
                                                badgeColor = '#10b981';
                                            }

                                            const isSystem = !l.user_id || l.user_email === 'System' || l.source_type === 'audit';

                                            return (
                                                <div key={l.id || i} className="log-entry-row" style={{ background: bgHighlight, borderLeft: lvl === 'error' ? '3px solid #ef4444' : 'none' }}>
                                                    <span className="log-time" title={l.created_at ? new Date(l.created_at).toLocaleString() : ''}>
                                                        [{l.created_at ? new Date(l.created_at).toISOString().replace('T', ' ').slice(0, 23) : 'LOG'}]
                                                    </span>
                                                    {isSystem ? (
                                                        <span 
                                                            className="log-user-tag" 
                                                            style={{ background: 'rgba(139, 92, 246, 0.16)', color: '#a78bfa', border: '1px solid rgba(139, 92, 246, 0.35)', fontWeight: 700, cursor: 'pointer' }}
                                                            title="Click to filter to System & Core Platform Engine"
                                                            onClick={() => handleUserFilterChange('system')}
                                                        >
                                                            ⚙️ {l.source_type === 'audit' ? 'System Audit' : 'Core Platform'}
                                                        </span>
                                                    ) : (
                                                        <span 
                                                            className="log-user-tag"
                                                            title={`Click to scope graphs & logs to ${l.user_email}`}
                                                            onClick={() => l.user_email && handleUserFilterChange(l.user_email)}
                                                            style={{ cursor: l.user_email ? 'pointer' : 'default' }}
                                                        >
                                                            👤 {l.user_name || l.user_email}
                                                        </span>
                                                    )}
                                                    {l.job_name && (
                                                        <span 
                                                            className="log-job-tag"
                                                            title={l.job_id ? `Click to scope graphs & logs to Job #${l.job_id}` : ''}
                                                            onClick={() => l.job_id && setSelectedJobFilter(String(l.job_id))}
                                                            style={{ cursor: l.job_id ? 'pointer' : 'default' }}
                                                        >
                                                            {l.job_id ? `⚡ Job #${l.job_id} (${l.job_name})` : l.job_name}
                                                        </span>
                                                    )}
                                                    {l.source_type === 'audit' && (
                                                        <span style={{ fontSize: '0.68rem', padding: '1px 5px', borderRadius: '4px', background: 'rgba(16, 185, 129, 0.15)', color: '#10b981', border: '1px solid rgba(16, 185, 129, 0.3)', fontWeight: 700 }}>
                                                            AUDIT
                                                        </span>
                                                    )}
                                                    <span style={{ color: badgeColor, fontWeight: 700, fontSize: '0.74rem', minWidth: '58px', flexShrink: 0 }}>
                                                        [{lvl.toUpperCase()}]
                                                    </span>
                                                    <span className="log-msg-text">
                                                        {l.message}
                                                    </span>
                                                </div>
                                            );
                                        })}
                                        {/* Scroll Anchor Target for Streaming Mode */}
                                        {logSortOrder === 'asc' && <div ref={terminalEndRef} style={{ height: '1px', width: '100%' }} />}
                                    </>
                                );
                            })()}

                            {/* Floating Jump to Latest Button if User Scrolled Up */}
                            {showJumpToLatest && (
                                <button
                                    className="btn-jump-latest"
                                    onClick={handleJumpToLatest}
                                >
                                    <ArrowDown size={13} /> Jump to Latest Logs ↓
                                </button>
                            )}
                        </div>
                    </div>
                )}

            </div>

            {/* Inspect Backend Logs Modal */}
            {inspectingJob && (
                <div className="log-inspector-modal">
                    <div className="log-inspector-box">
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                            <h3 style={{ margin: 0, color: 'var(--admin-text-main)', display: 'flex', alignItems: 'center', gap: '10px', fontSize: '1.2rem', fontWeight: 700 }}>
                                <Terminal size={22} color="#8b5cf6" /> Feature-Categorized Log Inspector — Job #{inspectingJob.id} ({inspectingJob.name})
                            </h3>
                            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                <button
                                    onClick={() => setModalAutoScroll(!modalAutoScroll)}
                                    title="Toggle automatic scrolling to latest logs in modal"
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        padding: '6px 12px',
                                        borderRadius: '8px',
                                        fontSize: '0.78rem',
                                        fontWeight: 700,
                                        background: modalAutoScroll ? 'rgba(16, 185, 129, 0.12)' : 'var(--admin-card-inner)',
                                        color: modalAutoScroll ? '#10b981' : 'var(--admin-text-muted)',
                                        border: `1px solid ${modalAutoScroll ? 'rgba(16, 185, 129, 0.4)' : 'var(--admin-card-border)'}`,
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease'
                                    }}
                                >
                                    <ArrowDown size={13} /> Auto-Scroll: {modalAutoScroll ? 'ON' : 'PAUSED'}
                                </button>
                                <button 
                                    className="btn-action-inspect"
                                    onClick={() => fetchJobLogsSilent(inspectingJob.id)}
                                >
                                    <RefreshCw size={13} className={loading ? 'spin' : ''} /> Refresh Logs
                                </button>
                                <button 
                                    className="btn-action-danger" 
                                    onClick={() => { setInspectingJob(null); setSelectedJobLogs(null); setLogCategoryFilter('all'); setLogSearchQuery(''); }}
                                >✕ Close Inspector</button>
                            </div>
                        </div>

                        <div style={{ fontSize: '0.85rem', color: 'var(--admin-text-secondary)', marginBottom: '14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                            <div>
                                Owner: <strong style={{ color: 'var(--admin-text-main)' }}>{inspectingJob.owner_email || 'Unknown'}</strong> | Engine: <strong style={{ color: '#0284c7' }}>{inspectingJob.source_type || 'Source'} → {inspectingJob.target_type || 'Target'}</strong> | Status: <span className={`pill-status ${inspectingJob.status}`}>{inspectingJob.status}</span>
                            </div>
                            <div style={{ fontSize: '0.76rem', color: '#10b981', display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 700 }}>
                                <span className="pulse-dot"></span> Polling Live Logs Every 2s
                            </div>
                        </div>

                        {/* Feature Category Tabs & Search Bar */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px', flexWrap: 'wrap', gap: '10px' }}>
                            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                                {[
                                    { id: 'all', label: 'All Streams' },
                                    { id: 'migration', label: 'Snapshot & CDC' },
                                    { id: 'health', label: 'Health & Probes' },
                                    { id: 'ssh', label: 'SSH & Tunnels' },
                                    { id: 'errors', label: 'Errors Only' }
                                ].map(cat => (
                                    <button
                                        key={cat.id}
                                        onClick={() => setLogCategoryFilter(cat.id)}
                                        style={{
                                            padding: '6px 12px',
                                            borderRadius: '8px',
                                            fontSize: '0.78rem',
                                            fontWeight: logCategoryFilter === cat.id ? 700 : 500,
                                            background: logCategoryFilter === cat.id ? 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)' : 'var(--admin-card-inner)',
                                            color: logCategoryFilter === cat.id ? '#ffffff' : 'var(--admin-text-secondary)',
                                            border: logCategoryFilter === cat.id ? '1px solid #8b5cf6' : '1px solid var(--admin-card-border)',
                                            cursor: 'pointer',
                                            transition: 'all 0.2s'
                                        }}
                                    >
                                        {cat.label}
                                    </button>
                                ))}
                            </div>

                            <div style={{ position: 'relative', width: '240px' }}>
                                <Search size={14} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: 'var(--admin-text-muted)' }} />
                                <input
                                    type="text"
                                    placeholder="Filter logs by keyword..."
                                    value={logSearchQuery}
                                    onChange={(e) => setLogSearchQuery(e.target.value)}
                                    style={{
                                        padding: '6px 12px 6px 32px',
                                        fontSize: '0.8rem',
                                        background: 'var(--admin-input-bg)',
                                        color: 'var(--admin-text-main)',
                                        border: '1px solid var(--admin-input-border)',
                                        borderRadius: '8px',
                                        width: '100%',
                                        boxSizing: 'border-box'
                                    }}
                                />
                            </div>
                        </div>

                        {/* Terminal Log Stream Window */}
                        <div 
                            ref={modalTerminalContainerRef} 
                            onScroll={handleModalTerminalScroll}
                            className="terminal-window" 
                            style={{ height: '380px', position: 'relative' }}
                        >
                            {(() => {
                                const filtered = (selectedJobLogs || []).filter(l => {
                                    const msg = (l.message || '').toLowerCase();
                                    const lvl = (l.level || '').toLowerCase();
                                    const search = logSearchQuery.toLowerCase();
                                    
                                    if (search && !msg.includes(search) && !lvl.includes(search)) return false;

                                    if (logCategoryFilter === 'migration') {
                                        return msg.includes('migrated') || msg.includes('streamed') || msg.includes('cdc') || msg.includes('table') || msg.includes('bulk') || msg.includes('full load') || msg.includes('initializing') || msg.includes('cutover');
                                    }
                                    if (logCategoryFilter === 'health') {
                                        return msg.includes('health') || msg.includes('probe') || msg.includes('ping') || msg.includes('socket') || msg.includes('connect');
                                    }
                                    if (logCategoryFilter === 'ssh') {
                                        return msg.includes('ssh') || msg.includes('tunnel') || msg.includes('bastion') || msg.includes('key') || msg.includes('permission') || msg.includes('timed out');
                                    }
                                    if (logCategoryFilter === 'errors') {
                                        return lvl === 'error' || lvl === 'warning' || msg.includes('failed') || msg.includes('error') || msg.includes('overflow') || msg.includes('denied') || msg.includes('exception');
                                    }
                                    return true;
                                });

                                if (filtered.length === 0) {
                                    return (
                                        <div style={{ color: 'var(--admin-text-muted)', textAlign: 'center', paddingTop: '60px' }}>
                                            No log events matching filter category "<strong>{logCategoryFilter}</strong>" {logSearchQuery ? `and query "${logSearchQuery}"` : ''}.
                                        </div>
                                    );
                                }

                                return (
                                    <>
                                        {filtered.map((l, i) => {
                                            const lvl = (l.level || 'info').toLowerCase();
                                            let badgeColor = isLight ? '#0284c7' : '#38bdf8';
                                            let bgHighlight = 'transparent';
                                            if (lvl === 'error' || (l.message || '').toLowerCase().includes('failed') || (l.message || '').toLowerCase().includes('error')) {
                                                badgeColor = '#ef4444';
                                                bgHighlight = isLight ? 'rgba(239, 68, 68, 0.08)' : 'rgba(239, 68, 68, 0.14)';
                                            } else if (lvl === 'warning' || (l.message || '').toLowerCase().includes('warning')) {
                                                badgeColor = '#f59e0b';
                                                bgHighlight = isLight ? 'rgba(245, 158, 11, 0.08)' : 'rgba(245, 158, 11, 0.1)';
                                            } else if (lvl === 'success' || (l.message || '').toLowerCase().includes('completed') || (l.message || '').toLowerCase().includes('success')) {
                                                badgeColor = '#10b981';
                                            }

                                            return (
                                                <div key={i} className="log-entry-row" style={{ background: bgHighlight, borderLeft: lvl === 'error' ? '3px solid #ef4444' : 'none' }}>
                                                    <span className="log-time">
                                                        [{l.created_at ? new Date(l.created_at).toISOString() : 'LOG'}]
                                                    </span>
                                                    <span style={{ color: badgeColor, fontWeight: 700, fontSize: '0.74rem', minWidth: '58px', flexShrink: 0 }}>
                                                        [{lvl.toUpperCase()}]
                                                    </span>
                                                    <span className="log-msg-text">
                                                        {l.message}
                                                    </span>
                                                </div>
                                            );
                                        })}
                                        <div ref={modalTerminalEndRef} style={{ height: '1px' }} />
                                    </>
                                );
                            })()}

                            {/* Floating Jump to Latest Button in Modal if Scrolled Up */}
                            {showModalJumpToLatest && (
                                <button
                                    className="btn-jump-latest"
                                    onClick={handleModalJumpToLatest}
                                >
                                    <ArrowDown size={13} /> Jump to Latest Logs ↓
                                </button>
                            )}
                        </div>

                        {/* Inspector Footer Actions */}
                        <div style={{ marginTop: '14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                            <span style={{ fontSize: '0.78rem', color: 'var(--admin-text-muted)' }}>
                                Showing {selectedJobLogs ? selectedJobLogs.length : 0} total log entries recorded for this job
                            </span>
                            <button
                                className="btn-action-inspect"
                                onClick={() => {
                                    const text = (selectedJobLogs || []).map(l => `[${l.created_at}] [${(l.level || 'info').toUpperCase()}]: ${l.message}`).join('\n');
                                    navigator.clipboard.writeText(text);
                                    alert('Logs copied to clipboard!');
                                }}
                            >
                                <Copy size={13} /> Copy All Logs to Clipboard
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Primary Database Encrypted Vault Broadview Modal */}
            {selectedConnectionVault && (
                <div className="log-inspector-modal">
                    <div className="log-inspector-box" style={{ maxWidth: '880px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                            <h3 style={{ margin: 0, color: 'var(--admin-text-main)', display: 'flex', alignItems: 'center', gap: '10px', fontSize: '1.2rem', fontWeight: 700 }}>
                                <KeyRound size={22} color="#8b5cf6" /> Primary DB Vault Broadview — Connection #{selectedConnectionVault.id} ({selectedConnectionVault.name})
                            </h3>
                            <button 
                                className="btn-action-danger" 
                                onClick={() => setSelectedConnectionVault(null)}
                            >✕ Close Broadview</button>
                        </div>
                        
                        <div style={{ fontSize: '0.85rem', color: 'var(--admin-text-secondary)', marginBottom: '16px' }}>
                            Owner: <strong style={{ color: 'var(--admin-text-main)' }}>{selectedConnectionVault.owner_email || 'System'}</strong> | Engine: <strong style={{ color: '#0284c7' }}>{selectedConnectionVault.type}</strong> | Host: <strong>{selectedConnectionVault.host}:{selectedConnectionVault.port}</strong> | DB: <strong>{selectedConnectionVault.database_name || selectedConnectionVault.database}</strong>
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.2rem', maxHeight: '65vh', overflowY: 'auto', paddingRight: '4px' }}>
                            {/* Section 1: Raw Encrypted Password Cipher */}
                            <div style={{ background: 'var(--admin-card-inner)', padding: '1.2rem', borderRadius: '12px', border: '1px solid var(--admin-card-border)' }}>
                                <div style={{ fontWeight: 700, color: '#8b5cf6', marginBottom: '8px', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <Lock size={16} color="#8b5cf6" /> Raw Encrypted Password Cipher (Stored in PostgreSQL `database_connections.password`)
                                </div>
                                {selectedConnectionVault.full_encrypted_password_db ? (
                                    <pre style={{ margin: 0, background: 'var(--admin-terminal-bg)', padding: '12px', borderRadius: '8px', fontSize: '0.82rem', color: '#38bdf8', overflowX: 'auto', border: '1px solid var(--admin-terminal-border)', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                                        {selectedConnectionVault.full_encrypted_password_db}
                                    </pre>
                                ) : (
                                    <div style={{ color: 'var(--admin-text-muted)', fontSize: '0.85rem' }}>No password cipher stored for this connection.</div>
                                )}
                            </div>

                            {/* Section 2: Raw Encrypted SSH Private Key Cipher */}
                            <div style={{ background: 'var(--admin-card-inner)', padding: '1.2rem', borderRadius: '12px', border: '1px solid var(--admin-card-border)' }}>
                                <div style={{ fontWeight: 700, color: '#a855f7', marginBottom: '8px', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <KeyRound size={16} color="#a855f7" /> Raw Encrypted SSH Private Key Cipher (Stored in PostgreSQL `database_connections.ssh_private_key`)
                                </div>
                                {selectedConnectionVault.full_encrypted_ssh_key_db ? (
                                    <pre style={{ margin: 0, background: 'var(--admin-terminal-bg)', padding: '12px', borderRadius: '8px', fontSize: '0.82rem', color: '#c084fc', overflowX: 'auto', border: '1px solid var(--admin-terminal-border)', whiteSpace: 'pre-wrap', wordBreak: 'break-all', maxHeight: '200px' }}>
                                        {selectedConnectionVault.full_encrypted_ssh_key_db}
                                    </pre>
                                ) : (
                                    <div style={{ color: 'var(--admin-text-muted)', fontSize: '0.85rem' }}>No SSH private key cipher stored for this connection.</div>
                                )}
                            </div>

                            {/* Section 3: Raw Encrypted SSH Passphrase Cipher */}
                            {selectedConnectionVault.full_encrypted_ssh_passphrase_db && (
                                <div style={{ background: 'var(--admin-card-inner)', padding: '1.2rem', borderRadius: '12px', border: '1px solid var(--admin-card-border)' }}>
                                    <div style={{ fontWeight: 700, color: '#6366f1', marginBottom: '8px', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <Lock size={16} color="#6366f1" /> Raw Encrypted SSH Passphrase Cipher (Stored in PostgreSQL `database_connections.ssh_passphrase`)
                                    </div>
                                    <pre style={{ margin: 0, background: 'var(--admin-terminal-bg)', padding: '12px', borderRadius: '8px', fontSize: '0.82rem', color: '#818cf8', overflowX: 'auto', border: '1px solid var(--admin-terminal-border)', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                                        {selectedConnectionVault.full_encrypted_ssh_passphrase_db}
                                    </pre>
                                </div>
                            )}

                            {/* Section 4: Complete DB Row Metadata */}
                            <div style={{ background: 'var(--admin-card-inner)', padding: '1.2rem', borderRadius: '12px', border: '1px solid var(--admin-card-border)' }}>
                                <div style={{ fontWeight: 700, color: 'var(--admin-text-main)', marginBottom: '8px', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <Database size={16} color="#8b5cf6" /> Complete PostgreSQL Database Row Metadata
                                </div>
                                <pre style={{ margin: 0, background: 'var(--admin-terminal-bg)', padding: '12px', borderRadius: '8px', fontSize: '0.78rem', color: '#94a3b8', overflowX: 'auto', border: '1px solid var(--admin-terminal-border)', whiteSpace: 'pre-wrap' }}>
                                    {JSON.stringify(selectedConnectionVault, null, 2)}
                                </pre>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Migration Job Telemetry & Detailed Metrics Modal */}
            {selectedTelemetryJob && (
                <MigrationDetailsModal
                    job={selectedTelemetryJob}
                    connections={connections}
                    onClose={() => setSelectedTelemetryJob(null)}
                />
            )}
        </div>
    );
}
